import React, { memo, useEffect, useState } from 'react';
import type { NavItem } from '../../../hooks/home/useActiveSection';

/**
 * 侧边栏（Figma 251:31 展开态 / 217:1139 折叠态 1:1 还原）
 *
 * 展开态 240px：
 *  - Logo 120x24 at (24,32)
 *  - 收起按钮 icon 16x16 at (208,36)
 *  - 炫彩"新建画布"按钮 224x48 at (8,96)，未悬停播放 10s 慢速动图，悬停切换为炫彩动图
 *  - 导航项 224x48 at x=8，48 高 + 4 间距：首页 y=152 / Bong作品 y=204 / 项目 y=256 / 工具集 y=308
 *    激活态 bg #222222 radius 8 + 左上激活标记 12x12 at (10,154)；悬停高亮 #2D2D2D（顶栏/tooltip 同 token）
 *
 * 折叠态 72px：
 *  - 方形 Logo 24x24 at (24,32)，点击展开；悬停切换为"展开"图标（284:31，竖线在右侧，16x16 居中）
 *  - 炫彩按钮 56x48（图标居中）
 *  - 导航项 56x48，图标 16x16 居中（x=28），悬停显示 tooltip（60x32 #2D2D2D radius 8，间距 8px）
 */

const ICONS = '/icons/home';
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
/* 炫彩"新建画布"按钮背景动图：悬停 = btn-gradient.gif；未悬停 = 10s 慢速版 btn-gradient-idle.gif */
const BTN_GIF = '/btn-gradient.gif';
const BTN_IDLE_GIF = '/btn-gradient-idle.gif';

interface SidebarProps {
  activeItem: NavItem;
  onNavigate: (item: NavItem) => void;
  onGoHome: () => void;
  onCreateCanvas: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const NAV_ITEMS: Array<{ key: NavItem; label: string; icon: string; top: number }> = [
  { key: 'home', label: '首页', icon: `${ICONS}/sidebar-home.svg`, top: 152 },
  { key: 'bongworks', label: 'Bong作品', icon: `${ICONS}/sidebar-bongtv.svg`, top: 204 },
  { key: 'canvas', label: '项目', icon: `${ICONS}/sidebar-project.svg`, top: 256 },
  { key: 'tools', label: '工具集', icon: `${ICONS}/sidebar-tools.svg`, top: 308 },
];

// 炫彩按钮背景：复刻 Figma btn（白色圆角底 + 内层炫彩渐变 0.65 透明度 blur 8px）
const GradientBtnBg: React.FC = () => (
  <>
    <div style={{ position: 'absolute', inset: 0, backgroundColor: '#ffffff', borderRadius: 8 }} />
    <div
      style={{
        position: 'absolute',
        left: 7,
        right: 7,
        top: 8,
        bottom: 8,
        borderRadius: 8,
        opacity: 0.65,
        filter: 'blur(8px)',
        background:
          'linear-gradient(90deg, #EAFCFF 0%, #A5E6FF 30%, #FFFFFF 50%, #FFB9D4 70%, #FFEFF5 100%)',
      }}
    />
  </>
);

const SidebarComponent: React.FC<SidebarProps> = ({
  activeItem,
  onNavigate,
  onGoHome,
  onCreateCanvas,
  collapsed,
  onToggleCollapsed,
}) => {
  const [hoveredKey, setHoveredKey] = useState<NavItem | null>(null);
  const [logoHovered, setLogoHovered] = useState(false);
  const [btnHovered, setBtnHovered] = useState(false);

  // 预加载悬停动图：首次悬停即时切换（未悬停动图随按钮常驻挂载、自然加载）
  useEffect(() => {
    const img = new Image();
    img.src = BTN_GIF;
  }, []);

  return (
    <aside
      className="fixed top-0 left-0 z-[200]"
      style={{
        width: collapsed ? 72 : 240,
        // 原生 fixed 流式布局：高度恒满视口（整页缩放体系已拆除）
        height: '100vh',
        backgroundColor: '#181818',
        transition: `width 0.25s ${EASE}`,
      }}
    >
      {/* Logo：展开横版 / 折叠方形，折叠态点击展开，展开态点击回首页；
          折叠态悬停切换为"展开"图标（Figma 284:31，竖线在右侧） */}
      <button
        onClick={() => (collapsed ? onToggleCollapsed() : onGoHome())}
        onMouseEnter={() => setLogoHovered(true)}
        onMouseLeave={() => setLogoHovered(false)}
        className="absolute cursor-pointer"
        aria-label={collapsed && logoHovered ? '展开侧边栏' : 'Bong AI'}
        style={{
          left: 24,
          top: 32,
          width: collapsed ? 24 : 120,
          height: 24,
          transition: `width 0.25s ${EASE}`,
        }}
      >
        {collapsed && logoHovered ? (
          <img
            src={`${ICONS}/sidebar-expand.svg`}
            alt=""
            className="pointer-events-none"
            style={{ width: 16, height: 16, margin: '4px auto', display: 'block' }}
          />
        ) : (
          <img
            src={collapsed ? `${ICONS}/logo-square.png` : `${ICONS}/logo-h.png`}
            alt="Bong AI"
            className="pointer-events-none"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        )}
      </button>

      {/* 收起按钮（仅展开态显示） */}
      {!collapsed && (
        <button
          onClick={onToggleCollapsed}
          className="absolute flex items-center justify-center cursor-pointer hover:bg-[#222222]"
          aria-label="收起侧边栏"
          style={{ left: 204, top: 32, width: 24, height: 24, borderRadius: 8 }}
        >
          <img src={`${ICONS}/sidebar-collapse.svg`} alt="" style={{ width: 16, height: 16 }} />
        </button>
      )}

      {/* 炫彩"新建画布"按钮：未悬停播放 10s 慢速炫彩动图，悬停切换为炫彩动图 */}
      <button
        onClick={() => onCreateCanvas()}
        onMouseEnter={() => setBtnHovered(true)}
        onMouseLeave={() => setBtnHovered(false)}
        className="absolute cursor-pointer"
        aria-label="新建画布"
        style={{
          left: 8,
          top: 96,
          width: collapsed ? 56 : 224,
          height: 48,
          transition: `width 0.25s ${EASE}`,
        }}
      >
        <GradientBtnBg />
        {/* 动态背景：未悬停播放 10s 慢速炫彩动图，悬停切换为炫彩动图——单 img 切
            src（悬停图已预加载进缓存、即时呈现；GIF 换 src 自动从首帧重播）；
            静态渐变底垫在下层，动图加载完成前按钮不露白 */}
        <img
          src={btnHovered ? BTN_GIF : BTN_IDLE_GIF}
          alt=""
          className="pointer-events-none"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 8,
          }}
        />
        <img
          src={`${ICONS}/sidebar-new-canvas.svg`}
          alt=""
          className="pointer-events-none"
          style={{
            position: 'absolute',
            left: collapsed ? 20 : 64,
            top: 16,
            width: 16,
            height: 16,
            transition: `left 0.25s ${EASE}`,
          }}
        />
        <span
          className="pointer-events-none"
          style={{
            position: 'absolute',
            left: 88,
            top: 17,
            color: '#141414',
            fontFamily: '"PingFang SC", sans-serif',
            fontWeight: 500,
            fontSize: 14,
            lineHeight: '14px',
            whiteSpace: 'nowrap',
            opacity: collapsed ? 0 : 1,
            transition: `opacity 0.2s ease, left 0.25s ${EASE}`,
          }}
        >
          新建画布
        </span>
      </button>

      {/* 导航项 */}
      {NAV_ITEMS.map((item) => {
        const active = activeItem === item.key;
        return (
          <button
            key={item.key}
            onClick={() => onNavigate(item.key)}
            onMouseEnter={() => setHoveredKey(item.key)}
            onMouseLeave={() => setHoveredKey(null)}
            className="absolute cursor-pointer"
            aria-label={item.label}
            style={{
              left: 8,
              top: item.top,
              width: collapsed ? 56 : 224,
              height: 48,
              borderRadius: 8,
              backgroundColor: active ? '#222222' : hoveredKey === item.key ? '#2D2D2D' : 'transparent',
              transition: `width 0.25s ${EASE}, background-color 0.2s ease`,
            }}
          >
            {active && (
              <img
                src={`${ICONS}/icon-active-mark.svg`}
                alt=""
                className="pointer-events-none"
                style={{ position: 'absolute', left: 2, top: 2, width: 12, height: 12 }}
              />
            )}
            <img
              src={item.icon}
              alt=""
              className="pointer-events-none"
              style={{
                position: 'absolute',
                left: collapsed ? 20 : 16,
                top: 16,
                width: 16,
                height: 16,
                transition: `left 0.25s ${EASE}`,
              }}
            />
            <span
              className="pointer-events-none"
              style={{
                position: 'absolute',
                left: 40,
                top: 17,
                color: '#FFFFFF',
                fontFamily: '"PingFang SC", sans-serif',
                fontWeight: 500,
                fontSize: 14,
                lineHeight: '14px',
                whiteSpace: 'nowrap',
                opacity: collapsed ? 0 : 1,
                transition: `opacity 0.2s ease, left 0.25s ${EASE}`,
              }}
            >
              {item.label}
            </span>

            {/* 折叠态悬停 tooltip（Figma 217:1139：60x32 #2D2D2D radius 8，位于 x=80 = 72px 侧边栏右缘 + 8px 间距，垂直居中） */}
            {collapsed && hoveredKey === item.key && (
              <span
                className="pointer-events-none"
                style={{
                  position: 'absolute',
                  left: 80,
                  top: 8,
                  padding: '9px 16px',
                  backgroundColor: '#2D2D2D',
                  borderRadius: 8,
                  color: '#FFFFFF',
                  fontFamily: '"PingFang SC", sans-serif',
                  fontWeight: 500,
                  fontSize: 14,
                  lineHeight: '14px',
                  whiteSpace: 'nowrap',
                  zIndex: 10,
                }}
              >
                {item.label}
              </span>
            )}
          </button>
        );
      })}
    </aside>
  );
};

export const Sidebar = memo(SidebarComponent);
